// src/pages/RMRDC/SupplierMan.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Select,
  FormControl,
  MenuItem,
  Snackbar,
  Alert,
  Slide,
  useMediaQuery,
  useTheme,
  alpha,
  Divider,
  Tooltip,
  Rating,
  Fade,
  Zoom,
  Badge,
  Stack,
  Skeleton,
  CircularProgress,
  Menu,
  ListItemIcon,
  ListItemText,
  Tab,
  Tabs,
} from '@mui/material';

// ============================================
// ICON IMPORTS
// ============================================
import {
  Dashboard as DashboardIcon,
  Storefront as StorefrontIcon,
  ShoppingCart as ShoppingCartIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  LockOutlined as LockOutlinedIcon,
  Verified as VerifiedIcon,
  Refresh as RefreshIcon,
  LocalShipping as LocalShippingIcon,
  Business as BusinessIcon,
  AttachMoney as AttachMoneyIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Inventory as InventoryIcon,
  Category as CategoryIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Receipt as ReceiptIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  List as ListIcon,
  GridView as GridIcon,
  LocationOn as LocationOnIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  ShoppingBag as ShoppingBagIcon,
  Block as BlockIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  Check as CheckIcon,
  Pending as PendingIcon,
} from '@mui/icons-material';

// ============================================
// IMPORT RMRDC COMPONENTS
// ============================================
import Preloader from '../../theme/Preloader';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footer from './components/Footer';
import { formatCurrency, formatDate } from '../buyers/components/utils';

// ============================================
// ENHANCED COLOR SCHEME
// ============================================
const COLORS = {
  primary: '#1A237E',
  primaryLight: '#283593',
  primaryDark: '#0D1445',
  secondary: '#00BCD4',
  accent: '#FF6F00',
  success: '#2E7D32',
  warning: '#E65100',
  error: '#C62828',
  info: '#01579B',
  whatsapp: '#25D366',
  grey: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
};

// ============================================
// API CONFIGURATION
// ============================================
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || '';

const API_ENDPOINTS = {
  SUPPLIERS: {
    LIST: `${API_BASE_URL}/rmrdc/suppliers.php`,
    STATS: `${API_BASE_URL}/rmrdc/suppliers.php?action=stats`,
    VERIFY: (id) => `${API_BASE_URL}/rmrdc/suppliers.php?action=verify&id=${id}`,
    TOGGLE_STATUS: (id) => `${API_BASE_URL}/rmrdc/suppliers.php?action=toggle_status&id=${id}`,
    DELETE: (id) => `${API_BASE_URL}/rmrdc/suppliers.php?id=${id}`,
    EXPORT: `${API_BASE_URL}/rmrdc/suppliers.php?action=export&format=csv`,
  },
  DOCUMENTS: {
    GET: (userId) => `${API_BASE_URL}/suppliers/get_supplier_documents.php?user_id=${userId}`,
  },
};

// ============================================
// LOGGER
// ============================================
const logger = {
  info: (message, data) => console.log(`[RMRDC-SUPPLIER] ✅ ${message}`, data || ''),
  error: (message, error) => console.error(`[RMRDC-SUPPLIER] ❌ ${message}`, error || ''),
  debug: (message, data) => console.debug(`[RMRDC-SUPPLIER] 🔍 ${message}`, data || ''),
  warn: (message, data) => console.warn(`[RMRDC-SUPPLIER] ⚠️ ${message}`, data || ''),
};

// ============================================
// HELPER FUNCTIONS
// ============================================
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  const base = IMAGE_BASE_URL.endsWith('/') ? IMAGE_BASE_URL : IMAGE_BASE_URL + '/';
  return `${base}${imagePath}`;
};

const getInitials = (name) => {
  if (!name) return 'S';
  const parts = name.split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const getDocumentIcon = (mimeType, fileName) => {
  if (!mimeType && !fileName) return <FileIcon />;
  const ext = fileName?.split('.').pop()?.toLowerCase() || '';
  if (mimeType?.includes('pdf') || ext === 'pdf') {
    return <PdfIcon sx={{ color: '#f44336' }} />;
  }
  if (mimeType?.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
    return <ImageIcon sx={{ color: '#4CAF50' }} />;
  }
  return <FileIcon sx={{ color: '#2196F3' }} />;
};

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'verified':
    case 'approved':
      return COLORS.success;
    case 'pending':
      return COLORS.warning;
    case 'rejected':
      return COLORS.error;
    default:
      return COLORS.grey[500];
  }
};

const getStatusLabel = (status) => {
  switch (status?.toLowerCase()) {
    case 'verified':
    case 'approved':
      return 'Verified';
    case 'pending':
      return 'Pending Review';
    case 'rejected':
      return 'Rejected';
    default:
      return status || 'Unknown';
  }
};

// ============================================
// DOCUMENT VIEWER COMPONENT
// ============================================
const DocumentViewer = ({ open, onClose, supplier, documents, loading }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  const getDocumentUrl = (filePath) => {
    if (!filePath) return null;
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      return filePath;
    }
    const base = API_BASE_URL.endsWith('/') ? API_BASE_URL : API_BASE_URL + '/';
    return `${base}${filePath}`;
  };

  const handleViewDocument = (doc) => {
    const url = getDocumentUrl(doc.file_path);
    if (url) {
      setSelectedDoc(doc);
      setPreviewUrl(url);
      setPreviewOpen(true);
    }
  };

  const handleDownloadDocument = (doc) => {
    const url = getDocumentUrl(doc.file_path);
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.original_name || doc.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ p: 3, pb: 2, borderBottom: `1px solid ${COLORS.grey[200]}` }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle', color: COLORS.primary }} />
                Supplier Documents
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {supplier?.company_name || 'Supplier'} • {documents?.length || 0} document(s)
              </Typography>
            </Box>
            <IconButton onClick={onClose} sx={{ color: COLORS.grey[400] }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : documents && documents.length > 0 ? (
            <Grid container spacing={2}>
              {documents.map((doc) => {
                const isImage = doc.mime_type?.includes('image') || 
                  ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(doc.file_name?.split('.').pop()?.toLowerCase());
                const isPdf = doc.mime_type?.includes('pdf') || 
                  doc.file_name?.toLowerCase().endsWith('.pdf');
                
                return (
                  <Grid size={{ xs: 12 }} key={doc.id}>
                    <Paper
                      sx={{
                        p: 2.5,
                        borderRadius: 3,
                        border: `1px solid ${COLORS.grey[200]}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: COLORS.primary,
                          boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          sx={{
                            width: 52,
                            height: 52,
                            bgcolor: alpha(COLORS.primary, 0.08),
                            borderRadius: 2,
                          }}
                        >
                          {getDocumentIcon(doc.mime_type, doc.file_name)}
                        </Avatar>
                        
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" fontWeight={600} noWrap>
                            {doc.original_name || doc.file_name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
                            <Typography variant="caption" color="textSecondary">
                              {formatFileSize(doc.file_size)}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Uploaded: {doc.uploaded_at_formatted || new Date(doc.uploaded_at).toLocaleDateString()}
                            </Typography>
                            <Chip
                              label={getStatusLabel(doc.status)}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.6rem',
                                bgcolor: alpha(getStatusColor(doc.status), 0.1),
                                color: getStatusColor(doc.status),
                                fontWeight: 600,
                              }}
                            />
                            {isImage && (
                              <Chip
                                label="Image"
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: '0.6rem',
                                  bgcolor: alpha(COLORS.info, 0.08),
                                  color: COLORS.info,
                                  fontWeight: 600,
                                }}
                              />
                            )}
                            {isPdf && (
                              <Chip
                                label="PDF"
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: '0.6rem',
                                  bgcolor: alpha('#f44336', 0.08),
                                  color: '#f44336',
                                  fontWeight: 600,
                                }}
                              />
                            )}
                          </Box>
                          {doc.rejection_reason && doc.status === 'rejected' && (
                            <Alert severity="error" sx={{ mt: 1, borderRadius: 2, py: 0.5 }}>
                              <Typography variant="caption" fontWeight={600}>
                                Rejection: {doc.rejection_reason}
                              </Typography>
                            </Alert>
                          )}
                        </Box>
                        
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="View Document">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleViewDocument(doc)}
                              startIcon={<VisibilityIcon fontSize="small" />}
                              sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                borderColor: COLORS.primary,
                                color: COLORS.primary,
                                '&:hover': {
                                  borderColor: COLORS.primaryDark,
                                  bgcolor: alpha(COLORS.primary, 0.05),
                                },
                              }}
                            >
                              View
                            </Button>
                          </Tooltip>
                          <Tooltip title="Download Document">
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleDownloadDocument(doc)}
                              startIcon={<DownloadIcon fontSize="small" />}
                              sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
                              }}
                            >
                              Download
                            </Button>
                          </Tooltip>
                        </Stack>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <DescriptionIcon sx={{ fontSize: 64, color: COLORS.grey[300], mb: 2 }} />
              <Typography variant="h6" color="textSecondary" fontWeight={600}>
                No documents uploaded
              </Typography>
              <Typography variant="body2" color="textSecondary">
                This supplier hasn't uploaded any documents yet.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${COLORS.grey[200]}` }}>
          <Button 
            onClick={onClose} 
            variant="outlined" 
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ p: 2.5, borderBottom: `1px solid ${COLORS.grey[200]}` }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: alpha(COLORS.primary, 0.08), color: COLORS.primary }}>
                {getDocumentIcon(selectedDoc?.mime_type, selectedDoc?.file_name)}
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  {selectedDoc?.original_name || selectedDoc?.file_name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {formatFileSize(selectedDoc?.file_size)} • {selectedDoc?.uploaded_at_formatted || ''}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => {
                  if (selectedDoc) handleDownloadDocument(selectedDoc);
                }}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
                }}
              >
                Download
              </Button>
              <IconButton onClick={() => setPreviewOpen(false)} sx={{ color: COLORS.grey[400] }}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          {previewUrl && (
            selectedDoc?.mime_type?.includes('image') || 
            ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(selectedDoc?.file_name?.split('.').pop()?.toLowerCase()) ? (
              <Box
                component="img"
                src={previewUrl}
                alt={selectedDoc?.original_name}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: 2,
                  boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  const parent = e.target.parentElement;
                  if (parent) {
                    parent.innerHTML = `
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <DescriptionIcon sx={{ fontSize: 64, color: '#BDBDBD' }} />
                        <Typography variant="body1" color="textSecondary">
                          Preview not available. Click download to view.
                        </Typography>
                        <Button 
                          variant="contained" 
                          onClick={() => window.open('${previewUrl}', '_blank')}
                          sx={{ mt: 2 }}
                        >
                          Open in New Tab
                        </Button>
                      </Box>
                    `;
                  }
                }}
              />
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <DescriptionIcon sx={{ fontSize: 80, color: COLORS.grey[300] }} />
                <Typography variant="h6" color="textSecondary" sx={{ mt: 2 }}>
                  Document Preview
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Preview not available for this file type.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => window.open(previewUrl, '_blank')}
                  sx={{ mt: 3, borderRadius: 2, textTransform: 'none' }}
                >
                  Open in New Tab
                </Button>
              </Box>
            )
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

// ============================================
// SUPPLIER CARD COMPONENT
// ============================================
const SupplierCard = ({ supplier, onViewProducts, onViewDocuments, onVerify, onToggleStatus, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const handleVerify = async () => {
    setActionLoading(true);
    await onVerify(supplier.id, !supplier.business_verified);
    setActionLoading(false);
  };

  const handleToggleStatus = async () => {
    setActionLoading(true);
    await onToggleStatus(supplier.id, supplier.user_active ? 0 : 1);
    setActionLoading(false);
  };

  const handleDelete = async () => {
    if (window.confirm(`Are you sure you want to delete ${supplier.company_name}?`)) {
      setActionLoading(true);
      await onDelete(supplier.id);
      setActionLoading(false);
    }
  };

  const hasDocuments = supplier.document_count > 0;

  return (
    <Zoom in style={{ transitionDelay: '100ms' }}>
      <Card
        sx={{
          borderRadius: 4,
          overflow: 'hidden',
          border: `1px solid ${COLORS.grey[200]}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          background: '#FFFFFF',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 16px 48px rgba(0,0,0,0.08)',
            borderColor: COLORS.primary,
          },
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: alpha(COLORS.primary, 0.08),
                  color: COLORS.primary,
                  fontWeight: 700,
                  fontSize: '1.2rem',
                  border: `2px solid ${alpha(COLORS.primary, 0.1)}`,
                }}
              >
                {getInitials(supplier.company_name)}
              </Avatar>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="h6" fontWeight={700} sx={{ color: COLORS.grey[900] }}>
                    {supplier.company_name}
                  </Typography>
                  {supplier.business_verified ? (
                    <Tooltip title="Verified Supplier">
                      <VerifiedIcon sx={{ color: COLORS.success, fontSize: 20 }} />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Not Verified">
                      <CancelIcon sx={{ color: COLORS.warning, fontSize: 20 }} />
                    </Tooltip>
                  )}
                  {!supplier.user_active && (
                    <Chip
                      label="INACTIVE"
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.55rem',
                        bgcolor: COLORS.error,
                        color: 'white',
                        fontWeight: 700,
                      }}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOnIcon sx={{ fontSize: 14, color: COLORS.grey[400] }} />
                  <Typography variant="caption" color="textSecondary">
                    {supplier.office_address || 'Location not specified'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                  <Chip
                    label={`${supplier.product_count || 0} Products`}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.6rem',
                      bgcolor: alpha(COLORS.info, 0.08),
                      color: COLORS.info,
                      fontWeight: 600,
                    }}
                  />
                  {supplier.total_orders > 0 && (
                    <Chip
                      label={`${supplier.total_orders} Orders`}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.6rem',
                        bgcolor: alpha(COLORS.success, 0.08),
                        color: COLORS.success,
                        fontWeight: 600,
                      }}
                    />
                  )}
                  {hasDocuments && (
                    <Chip
                      label={`${supplier.document_count} Documents`}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.6rem',
                        bgcolor: alpha(COLORS.secondary, 0.08),
                        color: COLORS.secondary,
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
              <Rating 
                value={parseFloat(supplier.avg_rating || 0)} 
                size="small" 
                readOnly 
                precision={0.5} 
              />
              <Typography variant="caption" color="textSecondary">
                {supplier.total_reviews || 0} reviews
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid size={{ xs: 3 }}>
              <Typography variant="caption" color="textSecondary" fontWeight={600} textTransform="uppercase" fontSize="0.6rem">
                Products
              </Typography>
              <Typography variant="h6" fontWeight={700} color={COLORS.primary} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                {supplier.product_count || 0}
              </Typography>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <Typography variant="caption" color="textSecondary" fontWeight={600} textTransform="uppercase" fontSize="0.6rem">
                Avg Order
              </Typography>
              <Typography variant="body2" fontWeight={700} color={COLORS.accent} sx={{ fontSize: { xs: '0.75rem', sm: '0.9rem' } }}>
                {formatCurrency(supplier.avg_order_value || 0)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <Typography variant="caption" color="textSecondary" fontWeight={600} textTransform="uppercase" fontSize="0.6rem">
                Stock
              </Typography>
              <Typography variant="h6" fontWeight={700} color={COLORS.success} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                {supplier.total_stock || 0}
              </Typography>
            </Grid>
            <Grid size={{ xs: 3 }}>
              <Typography variant="caption" color="textSecondary" fontWeight={600} textTransform="uppercase" fontSize="0.6rem">
                Docs
              </Typography>
              <Typography variant="h6" fontWeight={700} color={COLORS.secondary} sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                {supplier.document_count || 0}
              </Typography>
            </Grid>
          </Grid>

          {supplier.categories && supplier.categories.length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {supplier.categories.split(',').slice(0, 4).map((cat, i) => (
                <Chip
                  key={i}
                  label={cat.trim()}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.6rem',
                    bgcolor: alpha(COLORS.secondary, 0.08),
                    color: COLORS.secondary,
                    fontWeight: 500,
                  }}
                />
              ))}
              {supplier.categories.split(',').length > 4 && (
                <Chip
                  label={`+${supplier.categories.split(',').length - 4} more`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.6rem',
                    bgcolor: alpha(COLORS.grey[400], 0.08),
                    color: COLORS.grey[600],
                    fontWeight: 500,
                  }}
                />
              )}
            </Box>
          )}

          {supplier.recent_products && supplier.recent_products.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="textSecondary" fontWeight={600}>
                Recent Products
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, overflowX: 'auto', pb: 1 }}>
                {supplier.recent_products.slice(0, 3).map((product) => (
                  <Box
                    key={product.id}
                    sx={{
                      minWidth: 80,
                      p: 1,
                      borderRadius: 2,
                      bgcolor: COLORS.grey[50],
                      border: `1px solid ${COLORS.grey[200]}`,
                      textAlign: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      component="img"
                      src={getImageUrl(product.image) || `https://picsum.photos/seed/${product.id}/80/80`}
                      alt={product.name}
                      sx={{
                        width: 60,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 1,
                        mx: 'auto',
                        display: 'block',
                      }}
                      onError={(e) => {
                        e.target.src = `https://picsum.photos/seed/${product.id}/80/80`;
                      }}
                    />
                    <Typography variant="caption" sx={{ mt: 0.5, display: 'block', fontWeight: 500, fontSize: '0.6rem' }} noWrap>
                      {product.name}
                    </Typography>
                    <Typography variant="caption" color="primary" fontWeight={700} fontSize="0.6rem">
                      {formatCurrency(product.discountedPrice || product.price, product.currency)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
        <CardActions sx={{ p: 2.5, pt: 0, gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => onViewProducts(supplier)}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              flex: 1,
              minWidth: 80,
              background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
            }}
          >
            View Products
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => onViewDocuments(supplier)}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              flex: 1,
              minWidth: 80,
              borderColor: COLORS.secondary,
              color: COLORS.secondary,
              '&:hover': {
                borderColor: COLORS.secondary,
                bgcolor: alpha(COLORS.secondary, 0.05),
              },
            }}
          >
            <DescriptionIcon sx={{ fontSize: 16, mr: 0.5 }} />
            Docs ({supplier.document_count || 0})
          </Button>
          <Button
            size="small"
            variant={supplier.business_verified ? "outlined" : "contained"}
            onClick={handleVerify}
            disabled={actionLoading}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              flex: 1,
              minWidth: 80,
              ...(supplier.business_verified ? {
                borderColor: COLORS.success,
                color: COLORS.success,
              } : {
                bgcolor: COLORS.success,
                '&:hover': { bgcolor: COLORS.primaryDark },
              }),
            }}
          >
            {supplier.business_verified ? 'Unverify' : 'Verify'}
          </Button>
          <Button
            size="small"
            variant={supplier.user_active ? "outlined" : "contained"}
            onClick={handleToggleStatus}
            disabled={actionLoading}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              flex: 1,
              minWidth: 80,
              ...(supplier.user_active ? {
                borderColor: COLORS.warning,
                color: COLORS.warning,
              } : {
                bgcolor: COLORS.success,
                '&:hover': { bgcolor: COLORS.primaryDark },
              }),
            }}
          >
            {supplier.user_active ? 'Deactivate' : 'Activate'}
          </Button>
          <IconButton
            size="small"
            onClick={handleDelete}
            disabled={actionLoading}
            sx={{
              color: COLORS.error,
              border: `1px solid ${alpha(COLORS.error, 0.2)}`,
              borderRadius: 2,
              '&:hover': { bgcolor: alpha(COLORS.error, 0.05) },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={() => setExpanded(!expanded)}
            sx={{
              bgcolor: COLORS.grey[100],
              '&:hover': { bgcolor: COLORS.grey[200] },
            }}
          >
            {expanded ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
          </IconButton>
        </CardActions>
      </Card>
    </Zoom>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const SupplierMan = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ============================================
  // LOCAL STORAGE DATA
  // ============================================
  const userData = useMemo(() => {
    try {
      const data = localStorage.getItem('user_data');
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const userType = localStorage.getItem('user_type') || 'super_admin';

  // ============================================
  // STATE
  // ============================================
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [selectedTab, setSelectedTab] = useState('suppliers');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterVerified, setFilterVerified] = useState('all');
  const [sortBy, setSortBy] = useState('products');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(9);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [viewMode, setViewMode] = useState('grid');

  const [suppliers, setSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    verified: 0,
    totalProducts: 0,
    averageRating: 0,
  });

  const [supplierDetailDialog, setSupplierDetailDialog] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  // Document viewer state
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);

  // ============================================
  // FETCH DOCUMENT COUNTS FOR ALL SUPPLIERS
  // ============================================
  const fetchDocumentCounts = useCallback(async (suppliersList) => {
    if (!suppliersList || suppliersList.length === 0) return;
    
    try {
      const promises = suppliersList.map(async (supplier) => {
        try {
          const response = await fetch(API_ENDPOINTS.DOCUMENTS.GET(supplier.user_id || supplier.id), {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });
          const result = await response.json();
          return {
            supplierId: supplier.id,
            userId: supplier.user_id || supplier.id,
            count: result.success ? (result.data?.length || 0) : 0,
            documents: result.success ? (result.data || []) : [],
          };
        } catch (error) {
          logger.error(`Error fetching documents for supplier ${supplier.id}:`, error);
          return { supplierId: supplier.id, userId: supplier.user_id || supplier.id, count: 0, documents: [] };
        }
      });

      const results = await Promise.all(promises);
      
      setSuppliers(prevSuppliers => {
        return prevSuppliers.map(supplier => {
          const docData = results.find(r => r.supplierId === supplier.id);
          return {
            ...supplier,
            document_count: docData?.count || 0,
            _documents: docData?.documents || [],
          };
        });
      });

      logger.info(`✅ Updated document counts for ${results.length} suppliers`);
    } catch (error) {
      logger.error('Error fetching document counts:', error);
    }
  }, []);

  // ============================================
  // FETCH SUPPLIERS
  // ============================================
  const fetchSuppliers = useCallback(async () => {
    logger.info('Fetching suppliers...');
    setLoading(true);

    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) {
        throw new Error('User data not found. Please login again.');
      }

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      if (!userId) {
        throw new Error('User ID not found. Please login again.');
      }

      const url = `${API_ENDPOINTS.SUPPLIERS.LIST}?user_id=${userId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success && result.data) {
        let suppliersData = [];
        
        if (Array.isArray(result.data)) {
          suppliersData = result.data;
        } else if (result.data.suppliers) {
          suppliersData = result.data.suppliers;
        }
        
        const suppliersWithDocCount = suppliersData.map(s => ({
          ...s,
          document_count: 0,
          _documents: [],
        }));
        
        setSuppliers(suppliersWithDocCount);
        
        const statsData = {
          total: suppliersWithDocCount.length,
          verified: suppliersWithDocCount.filter(s => s.business_verified).length,
          totalProducts: suppliersWithDocCount.reduce((sum, s) => sum + (s.product_count || 0), 0),
          averageRating: suppliersWithDocCount.reduce((sum, s) => sum + parseFloat(s.avg_rating || 0), 0) / (suppliersWithDocCount.length || 1),
        };
        setStats(statsData);
        
        logger.info(`✅ Loaded ${suppliersWithDocCount.length} suppliers`);
        
        if (suppliersWithDocCount.length > 0) {
          await fetchDocumentCounts(suppliersWithDocCount);
        }
      } else {
        throw new Error(result.message || 'Failed to fetch suppliers');
      }
    } catch (error) {
      logger.error('Error fetching suppliers:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to load suppliers. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [fetchDocumentCounts]);

  // ============================================
  // FETCH SUPPLIER DOCUMENTS (on demand)
  // ============================================
  const fetchSupplierDocuments = useCallback(async (supplierId) => {
    const cachedSupplier = suppliers.find(s => s.id === supplierId);
    if (cachedSupplier?._documents && cachedSupplier._documents.length > 0) {
      logger.info(`Using cached documents for supplier ${supplierId}`);
      setDocuments(cachedSupplier._documents);
      return;
    }

    setDocumentsLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.DOCUMENTS.GET(supplierId), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      
      if (result.success) {
        const docData = result.data || [];
        setDocuments(docData);
        
        setSuppliers(prevSuppliers => 
          prevSuppliers.map(s => 
            s.id === supplierId 
              ? { ...s, _documents: docData, document_count: docData.length }
              : s
          )
        );
        
        logger.info(`Loaded ${docData.length} documents for supplier ${supplierId}`);
      } else {
        setDocuments([]);
        logger.warn('No documents found for supplier');
      }
    } catch (error) {
      logger.error('Error fetching documents:', error);
      setDocuments([]);
      setSnackbar({
        open: true,
        message: 'Failed to load documents. Please try again.',
        severity: 'error',
      });
    } finally {
      setDocumentsLoading(false);
    }
  }, [suppliers]);

  // ============================================
  // VERIFY SUPPLIER
  // ============================================
  const verifySupplier = useCallback(async (supplierId, verified) => {
    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) return;

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      const response = await fetch(`${API_ENDPOINTS.SUPPLIERS.VERIFY(supplierId)}&user_id=${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ verified: verified ? 1 : 0 }),
      });

      const result = await response.json();
      
      if (result.success) {
        setSnackbar({ 
          open: true, 
          message: result.message || `Supplier ${verified ? 'verified' : 'unverified'} successfully`, 
          severity: 'success' 
        });
        fetchSuppliers();
      } else {
        throw new Error(result.message || 'Failed to update verification status');
      }
    } catch (error) {
      logger.error('Error verifying supplier:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to update verification', severity: 'error' });
    }
  }, [fetchSuppliers]);

  // ============================================
  // TOGGLE SUPPLIER STATUS
  // ============================================
  const toggleSupplierStatus = useCallback(async (supplierId, status) => {
    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) return;

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      const response = await fetch(`${API_ENDPOINTS.SUPPLIERS.TOGGLE_STATUS(supplierId)}&user_id=${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });

      const result = await response.json();
      
      if (result.success) {
        setSnackbar({ 
          open: true, 
          message: result.message || `Supplier ${status ? 'activated' : 'deactivated'} successfully`, 
          severity: 'success' 
        });
        fetchSuppliers();
      } else {
        throw new Error(result.message || 'Failed to update supplier status');
      }
    } catch (error) {
      logger.error('Error toggling supplier status:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to update status', severity: 'error' });
    }
  }, [fetchSuppliers]);

  // ============================================
  // DELETE SUPPLIER
  // ============================================
  const deleteSupplier = useCallback(async (supplierId) => {
    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) return;

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      const response = await fetch(`${API_ENDPOINTS.SUPPLIERS.DELETE(supplierId)}&user_id=${userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      
      if (result.success) {
        setSnackbar({ open: true, message: result.message || 'Supplier deleted successfully', severity: 'success' });
        fetchSuppliers();
      } else {
        throw new Error(result.message || 'Failed to delete supplier');
      }
    } catch (error) {
      logger.error('Error deleting supplier:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to delete supplier', severity: 'error' });
    }
  }, [fetchSuppliers]);

  // ============================================
  // EXPORT SUPPLIERS
  // ============================================
  const exportSuppliers = useCallback(async () => {
    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) return;

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      const response = await fetch(`${API_ENDPOINTS.SUPPLIERS.EXPORT}&user_id=${userId}`, {
        method: 'GET',
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `suppliers_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      setSnackbar({ open: true, message: 'Suppliers exported successfully', severity: 'success' });
    } catch (error) {
      logger.error('Error exporting suppliers:', error);
      setSnackbar({ open: true, message: 'Failed to export suppliers', severity: 'error' });
    }
  }, []);

  // ============================================
  // EFFECTS
  // ============================================
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token || (userType !== 'super_admin' && userType !== 'rmrdc')) {
      navigate('/RawAuth');
      return;
    }
    fetchSuppliers();
  }, [fetchSuppliers, navigate, userType]);

  // ============================================
  // FILTERING & SORTING
  // ============================================
  useEffect(() => {
    let result = [...suppliers];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(supplier => 
        (supplier.company_name && supplier.company_name.toLowerCase().includes(term)) ||
        (supplier.office_address && supplier.office_address.toLowerCase().includes(term)) ||
        (supplier.categories && supplier.categories.toLowerCase().includes(term))
      );
    }

    if (filterVerified === 'verified') {
      result = result.filter(s => s.business_verified);
    } else if (filterVerified === 'unverified') {
      result = result.filter(s => !s.business_verified);
    }

    switch (sortBy) {
      case 'products':
        result.sort((a, b) => (b.product_count || 0) - (a.product_count || 0));
        break;
      case 'rating':
        result.sort((a, b) => parseFloat(b.avg_rating || 0) - parseFloat(a.avg_rating || 0));
        break;
      case 'orders':
        result.sort((a, b) => (b.total_orders || 0) - (a.total_orders || 0));
        break;
      case 'name':
        result.sort((a, b) => (a.company_name || '').localeCompare(b.company_name || ''));
        break;
      default:
        break;
    }

    setFilteredSuppliers(result);
  }, [suppliers, searchTerm, filterVerified, sortBy]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleLogout = useCallback(() => {
    localStorage.clear();
    navigate('/RawAuth');
    setSnackbar({ open: true, message: 'Logged out successfully', severity: 'info' });
  }, [navigate]);

  const handleRefresh = useCallback(() => {
    fetchSuppliers();
    setLastRefreshed(new Date());
    setSnackbar({ open: true, message: 'Data refreshed', severity: 'info' });
  }, [fetchSuppliers]);

  const handleViewProducts = useCallback((supplier) => {
    setSelectedSupplier(supplier);
    setSupplierDetailDialog(true);
  }, []);

  const handleViewDocuments = useCallback((supplier) => {
    setSelectedSupplier(supplier);
    setDocumentViewerOpen(true);
    fetchSupplierDocuments(supplier.id);
  }, [fetchSupplierDocuments]);

  // ============================================
  // RENDER FUNCTIONS
  // ============================================
  const renderStats = () => (
    <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
      <Grid size={{ xs: 6, sm: 3 }}>
        <Card sx={{ borderRadius: 3, p: 2.5, textAlign: 'center', bgcolor: alpha(COLORS.primary, 0.04), border: `1px solid ${alpha(COLORS.primary, 0.08)}` }}>
          <Typography variant="h4" fontWeight={800} color={COLORS.primary}>{stats.total}</Typography>
          <Typography variant="caption" color="textSecondary" fontWeight={600}>Total Suppliers</Typography>
        </Card>
      </Grid>
      <Grid size={{ xs: 6, sm: 3 }}>
        <Card sx={{ borderRadius: 3, p: 2.5, textAlign: 'center', bgcolor: alpha(COLORS.success, 0.04), border: `1px solid ${alpha(COLORS.success, 0.08)}` }}>
          <Typography variant="h4" fontWeight={800} color={COLORS.success}>{stats.verified}</Typography>
          <Typography variant="caption" color="textSecondary" fontWeight={600}>Verified</Typography>
        </Card>
      </Grid>
      <Grid size={{ xs: 6, sm: 3 }}>
        <Card sx={{ borderRadius: 3, p: 2.5, textAlign: 'center', bgcolor: alpha(COLORS.info, 0.04), border: `1px solid ${alpha(COLORS.info, 0.08)}` }}>
          <Typography variant="h4" fontWeight={800} color={COLORS.info}>{stats.totalProducts}</Typography>
          <Typography variant="caption" color="textSecondary" fontWeight={600}>Total Products</Typography>
        </Card>
      </Grid>
      <Grid size={{ xs: 6, sm: 3 }}>
        <Card sx={{ borderRadius: 3, p: 2.5, textAlign: 'center', bgcolor: alpha(COLORS.warning, 0.04), border: `1px solid ${alpha(COLORS.warning, 0.08)}` }}>
          <Typography variant="h4" fontWeight={800} color={COLORS.warning}>{stats.averageRating.toFixed(1)}</Typography>
          <Typography variant="caption" color="textSecondary" fontWeight={600}>Avg Rating</Typography>
        </Card>
      </Grid>
    </Grid>
  );

  const renderFilters = () => (
    <Paper sx={{ p: 2.5, mb: 3.5, borderRadius: 4, border: `1px solid ${COLORS.grey[200]}`, background: '#FFFFFF' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Search suppliers by name, location..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: COLORS.grey[500] }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <ClearIcon fontSize="small" sx={{ color: COLORS.grey[500] }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 200, flex: 1 }}
        />
        
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={filterVerified}
            onChange={(e) => setFilterVerified(e.target.value)}
            displayEmpty
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            <MenuItem value="all" sx={{ fontWeight: 600 }}>All Suppliers</MenuItem>
            <MenuItem value="verified" sx={{ fontWeight: 500 }}>Verified</MenuItem>
            <MenuItem value="unverified" sx={{ fontWeight: 500 }}>Unverified</MenuItem>
          </Select>
        </FormControl>
        
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            displayEmpty
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            <MenuItem value="products" sx={{ fontWeight: 600 }}>Most Products</MenuItem>
            <MenuItem value="rating" sx={{ fontWeight: 500 }}>Highest Rated</MenuItem>
            <MenuItem value="orders" sx={{ fontWeight: 500 }}>Most Orders</MenuItem>
            <MenuItem value="name" sx={{ fontWeight: 500 }}>Name A-Z</MenuItem>
          </Select>
        </FormControl>
        
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto', alignItems: 'center' }}>
        
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} size="small" sx={{ bgcolor: COLORS.grey[100], borderRadius: 2 }}>
              <RefreshIcon sx={{ color: COLORS.grey[700] }} />
            </IconButton>
          </Tooltip>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => setViewMode('grid')}
              sx={{
                bgcolor: viewMode === 'grid' ? alpha(COLORS.primary, 0.08) : 'transparent',
                color: viewMode === 'grid' ? COLORS.primary : COLORS.grey[400],
              }}
            >
              <GridIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setViewMode('list')}
              sx={{
                bgcolor: viewMode === 'list' ? alpha(COLORS.primary, 0.08) : 'transparent',
                color: viewMode === 'list' ? COLORS.primary : COLORS.grey[400],
              }}
            >
              <ListIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Paper>
  );

  const renderGridView = () => (
    <Grid container spacing={3}>
      {filteredSuppliers.length === 0 ? (
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
            <BusinessIcon sx={{ fontSize: 64, color: COLORS.grey[300], mb: 2 }} />
            <Typography variant="h6" color="textSecondary" fontWeight={600}>No suppliers found</Typography>
            <Typography variant="body2" color="textSecondary">Try adjusting your search or filters</Typography>
          </Paper>
        </Grid>
      ) : (
        filteredSuppliers
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map((supplier) => (
            <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={supplier.id}>
              <SupplierCard
                supplier={supplier}
                onViewProducts={handleViewProducts}
                onViewDocuments={handleViewDocuments}
                onVerify={verifySupplier}
                onToggleStatus={toggleSupplierStatus}
                onDelete={deleteSupplier}
              />
            </Grid>
          ))
      )}
    </Grid>
  );

  const renderListView = () => (
    <TableContainer component={Paper} sx={{ borderRadius: 4, border: `1px solid ${COLORS.grey[200]}` }}>
      <Table>
        <TableHead sx={{ bgcolor: COLORS.grey[50] }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700] }}>Supplier</TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700] }}>Products</TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700] }}>Docs</TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700] }}>Rating</TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700] }}>Orders</TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700] }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700] }} align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredSuppliers.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                <Typography color="textSecondary">No suppliers found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredSuppliers
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((supplier) => (
                <TableRow key={supplier.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 36, height: 36, bgcolor: alpha(COLORS.primary, 0.08), color: COLORS.primary, fontWeight: 700, fontSize: '0.8rem' }}>
                        {getInitials(supplier.company_name)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={700}>
                          {supplier.company_name}
                          {supplier.business_verified ? (
                            <VerifiedIcon sx={{ fontSize: 14, color: COLORS.success, ml: 0.5, verticalAlign: 'middle' }} />
                          ) : (
                            <CancelIcon sx={{ fontSize: 14, color: COLORS.warning, ml: 0.5, verticalAlign: 'middle' }} />
                          )}
                          {!supplier.user_active && (
                            <Chip
                              label="Inactive"
                              size="small"
                              sx={{ height: 16, fontSize: '0.5rem', ml: 0.5, bgcolor: COLORS.error, color: 'white' }}
                            />
                          )}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {supplier.office_address || 'No address'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} color={COLORS.primary}>
                      {supplier.product_count || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={supplier.document_count || 0}
                      size="small"
                      sx={{
                        bgcolor: alpha(COLORS.secondary, 0.08),
                        color: COLORS.secondary,
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Rating value={parseFloat(supplier.avg_rating || 0)} size="small" readOnly precision={0.5} />
                      <Typography variant="caption" fontWeight={600}>
                        {parseFloat(supplier.avg_rating || 0).toFixed(1)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} color={COLORS.accent}>
                      {supplier.total_orders || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={supplier.business_verified ? 'Verified' : 'Unverified'}
                      size="small"
                      sx={{
                        bgcolor: supplier.business_verified ? alpha(COLORS.success, 0.1) : alpha(COLORS.warning, 0.1),
                        color: supplier.business_verified ? COLORS.success : COLORS.warning,
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title="View Products">
                        <IconButton
                          size="small"
                          onClick={() => handleViewProducts(supplier)}
                          sx={{ color: COLORS.primary }}
                        >
                          <StorefrontIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View Documents">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDocuments(supplier)}
                          sx={{ color: COLORS.secondary }}
                        >
                          <DescriptionIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={supplier.business_verified ? 'Unverify' : 'Verify'}>
                        <IconButton
                          size="small"
                          onClick={() => verifySupplier(supplier.id, !supplier.business_verified)}
                          sx={{ color: supplier.business_verified ? COLORS.warning : COLORS.success }}
                        >
                          {supplier.business_verified ? <CancelIcon fontSize="small" /> : <VerifiedIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={supplier.user_active ? 'Deactivate' : 'Activate'}>
                        <IconButton
                          size="small"
                          onClick={() => toggleSupplierStatus(supplier.id, supplier.user_active ? 0 : 1)}
                          sx={{ color: supplier.user_active ? COLORS.warning : COLORS.success }}
                        >
                          {supplier.user_active ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => deleteSupplier(supplier.id)}
                          sx={{ color: COLORS.error }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[6, 9, 12, 24]}
        component="div"
        count={filteredSuppliers.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </TableContainer>
  );

  const renderSupplierDetailDialog = () => (
    <Dialog
      open={supplierDetailDialog}
      onClose={() => setSupplierDetailDialog(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4 } }}
    >
      {selectedSupplier && (
        <>
          <DialogTitle sx={{ p: 3, pb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: alpha(COLORS.primary, 0.08),
                    color: COLORS.primary,
                    fontWeight: 700,
                    fontSize: '1rem',
                  }}
                >
                  {getInitials(selectedSupplier.company_name)}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    {selectedSupplier.company_name}
                    {selectedSupplier.business_verified ? (
                      <VerifiedIcon sx={{ fontSize: 20, color: COLORS.success, ml: 0.5, verticalAlign: 'middle' }} />
                    ) : (
                      <CancelIcon sx={{ fontSize: 20, color: COLORS.warning, ml: 0.5, verticalAlign: 'middle' }} />
                    )}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {selectedSupplier.office_address || 'No address specified'}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setSupplierDetailDialog(false);
                    handleViewDocuments(selectedSupplier);
                  }}
                  startIcon={<DescriptionIcon />}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Documents
                </Button>
                <IconButton onClick={() => setSupplierDetailDialog(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2, bgcolor: COLORS.grey[50], borderRadius: 2 }}>
                  <Typography variant="caption" color="textSecondary" fontWeight={600}>Contact Information</Typography>
                  {selectedSupplier.email && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <EmailIcon sx={{ fontSize: 16, color: COLORS.grey[400] }} />
                      <Typography variant="body2">{selectedSupplier.email}</Typography>
                    </Box>
                  )}
                  {selectedSupplier.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <PhoneIcon sx={{ fontSize: 16, color: COLORS.grey[400] }} />
                      <Typography variant="body2">{selectedSupplier.phone}</Typography>
                    </Box>
                  )}
                  {selectedSupplier.rc_number && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <BusinessIcon sx={{ fontSize: 16, color: COLORS.grey[400] }} />
                      <Typography variant="body2">RC: {selectedSupplier.rc_number}</Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2, bgcolor: COLORS.grey[50], borderRadius: 2 }}>
                  <Typography variant="caption" color="textSecondary" fontWeight={600}>Business Details</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                    <InventoryIcon sx={{ fontSize: 16, color: COLORS.grey[400] }} />
                    <Typography variant="body2">{selectedSupplier.product_count || 0} Products</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <ShoppingBagIcon sx={{ fontSize: 16, color: COLORS.grey[400] }} />
                    <Typography variant="body2">{selectedSupplier.total_orders || 0} Orders</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <AttachMoneyIcon sx={{ fontSize: 16, color: COLORS.grey[400] }} />
                    <Typography variant="body2">Avg Order: {formatCurrency(selectedSupplier.avg_order_value || 0)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <DescriptionIcon sx={{ fontSize: 16, color: COLORS.grey[400] }} />
                    <Typography variant="body2">{selectedSupplier.document_count || 0} Documents</Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {selectedSupplier.categories && selectedSupplier.categories.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Categories
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selectedSupplier.categories.split(',').map((cat, i) => (
                    <Chip key={i} label={cat.trim()} size="small" />
                  ))}
                </Box>
              </Box>
            )}

            {selectedSupplier.top_products && selectedSupplier.top_products.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Top Products
                </Typography>
                <Grid container spacing={2}>
                  {selectedSupplier.top_products.map((product) => (
                    <Grid size={{ xs: 6, sm: 4 }} key={product.id}>
                      <Card sx={{ borderRadius: 2, border: `1px solid ${COLORS.grey[200]}` }}>
                        <Box
                          component="img"
                          src={getImageUrl(product.image) || `https://picsum.photos/seed/${product.id}/120/120`}
                          alt={product.name}
                          sx={{
                            width: '100%',
                            height: 100,
                            objectFit: 'cover',
                          }}
                          onError={(e) => {
                            e.target.src = `https://picsum.photos/seed/${product.id}/120/120`;
                          }}
                        />
                        <CardContent sx={{ p: 1.5 }}>
                          <Typography variant="caption" fontWeight={600} noWrap>
                            {product.name}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" fontWeight={700} color="primary">
                              {formatCurrency(product.price, product.currency)}
                            </Typography>
                            <Chip
                              label={`${product.order_count || 0} sold`}
                              size="small"
                              sx={{ height: 18, fontSize: '0.5rem' }}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setSupplierDetailDialog(false)} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );

  // ============================================
  // MAIN RENDER
  // ============================================
  if (loading && suppliers.length === 0) {
    return <Preloader text="Loading Suppliers..." size={60} fullScreen />;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: COLORS.grey[50] }}>
      <Sidebar
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        isMobile={isMobile}
        selectedTab={selectedTab}
        onTabChange={setSelectedTab}
        userData={userData}
        onLogout={handleLogout}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          width: { xs: '100%', md: 'calc(100% - 280px)' },
        }}
      >
        <Header
          isMobile={isMobile}
          onDrawerToggle={() => setDrawerOpen(true)}
          selectedTab={selectedTab}
          menuItems={[]}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          cartItemCount={0}
          onCartOpen={() => {}}
          notifications={[]}
          userData={userData}
          onLogout={handleLogout}
          onChangePassword={() => {}}
        />

        <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, overflow: 'auto' }}>
          <Box sx={{ mb: 3.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h4" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <BusinessIcon sx={{ color: COLORS.primary, fontSize: 32 }} />
                  Supplier Management
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {filteredSuppliers.length} suppliers found • Last updated: {lastRefreshed.toLocaleString()}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {renderStats()}
          {renderFilters()}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
            <Typography variant="caption" color="textSecondary" fontWeight={600}>
              Showing {Math.min(filteredSuppliers.length, rowsPerPage)} of {filteredSuppliers.length} suppliers
            </Typography>
          </Box>

          {viewMode === 'grid' ? renderGridView() : renderListView()}
        </Box>

        <Footer />
      </Box>

      {renderSupplierDetailDialog()}

      <DocumentViewer
        open={documentViewerOpen}
        onClose={() => {
          setDocumentViewerOpen(false);
          setDocuments([]);
        }}
        supplier={selectedSupplier}
        documents={documents}
        loading={documentsLoading}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ borderRadius: 3, boxShadow: '0 16px 48px rgba(0,0,0,0.12)', fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SupplierMan;